import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import {
  getLocalizationVerificationCode,
  getLocalizedStations,
} from '../../actions/contentActions'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import { LocalizationAuthStatus } from '../../components/VerifyLocation/types'
import { useLocalizePoll } from '../../components/VerifyLocation/useLocalizePoll'
import useAsync from '../../hooks/useAsync'
import { useHybridFocusable } from '../../hooks/useHybridFocusable/index'
import { useAppSelector } from '../../hooks/useStore'
import tts from '../../lib/TTS'
import type { LocalizationCodeResponse, Station } from '../Livestream/types'

const COMPONENT_FOCUS_KEY = 'LOCATE_PROMPT'
const GET_NEW_CODE_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_GET_NEW_CODE_BUTTON`

const LocatePrompt = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const initialState = location.state as LocalizationCodeResponse
  const [currentState, setCurrentState] =
    useState<LocalizationCodeResponse | null>(initialState)
  const hasAnnouncedAuthorizationExpiration = useRef(false)

  const { ref, focusKey } = useHybridFocusable({
    focusKey: COMPONENT_FOCUS_KEY,
    trackChildren: true,
  })
  const { station_id: stationId, call_sign: callSign } = useAppSelector(
    (state) => state.station
  )
  const { value, execute: getLocalizationData } = useAsync<
    LocalizationCodeResponse,
    string
  >({
    callback: (stationId) => getLocalizationVerificationCode(stationId),
    dependencies: [],
    auto: false,
    initialArgs: stationId,
    preserveValueWhileLoading: false,
  })

  const {
    data,
    error: errorPolling,
    isAuthorizationExpired,
  } = useLocalizePoll(currentState!)

  const handleNavigateToError = async (
    status: LocalizationAuthStatus,
    hasError: boolean = false
  ) => {
    try {
      const localizedStations = await getLocalizedStations()
      const stations: Array<Station> = localizedStations?.stations || []

      navigate('/localization-error', {
        state: {
          status,
          callSign,
          hasError,
          stations,
        },
        replace: true,
      })
    } catch (err) {
      navigate('/localization-error', {
        state: {
          status,
          callSign,
          hasError: true,
          stations: [],
        },
        replace: true,
      })
    }
  }

  const handleLocalizationAuthorized = () => {
    tts.stopRead()
    tts.pushTextToRead(
      'Location verified successfully. Returning to livestream.'
    )
    navigate('/livestream', { replace: true })
  }

  useEffect(() => {
    try {
      if (
        data &&
        data.status === LocalizationAuthStatus.AUTHORIZED &&
        !errorPolling
      ) {
        handleLocalizationAuthorized()
      } else if (errorPolling) {
        handleNavigateToError(LocalizationAuthStatus.ERROR, true)
      } else if (
        !hasAnnouncedAuthorizationExpiration.current &&
        isAuthorizationExpired()
      ) {
        hasAnnouncedAuthorizationExpiration.current = true
        tts.stopRead()
        tts.pushTextToRead(
          'The authorization has expired. Please get a new code to verify your location.'
        )
      } else if (
        (data &&
          (data.status === LocalizationAuthStatus.UNAUTHORIZED ||
            data.status === LocalizationAuthStatus.ERROR)) ||
        errorPolling
      ) {
        handleNavigateToError(LocalizationAuthStatus.UNAUTHORIZED, false)
      }
    } catch (err) {
      console.error('Error handling localization result:', err)
      tts.stopRead()
      tts.pushTextToRead(
        'There was an error verifying your location. Please try again.'
      )

      handleNavigateToError(LocalizationAuthStatus.ERROR, true)
    }
  }, [data, errorPolling, isAuthorizationExpired])

  useKeyDown([KeyTypeEnum.Back], () => {
    tts.pushTextToRead('Going back to livestream')
    navigate('/livestream')
  })

  // Workaround to allow new focus components to work with spatial navigation and old components work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  useEffect(() => {
    if (value) {
      setCurrentState(value)
    }
  }, [value])

  useEffect(() => {
    hasAnnouncedAuthorizationExpiration.current = false
  }, [currentState?.code])

  useEffect(() => {
    if (currentState?.code) {
      tts.stopRead()

      const codeValue = currentState.code
        ? currentState.code
        : String(currentState.code ?? '')
      const codeWithSpaces = codeValue.split('').join(' , ')

      tts.addTextToRead(`Verify Your Location. Step 1. From a mobile device or computer, visit: ${currentState.authorization_url}.
      Step 2. Enter code: ${codeWithSpaces}. Once your location is verified, this screen will automatically update within 30 seconds. 
      The code again is: ${codeWithSpaces}. If you have any issues with the code listed above, please use the button below to get a new code ..... Focused on Get New Code Button
      `)
    }
    return () => tts.stopRead()
  }, [currentState?.code, currentState?.authorization_url])

  const handleGetNewCode = () => {
    tts.pushTextToRead('Selected Get New Code Button')
    getLocalizationData(stationId)
  }

  if (!currentState) {
    return (
      <div className="locate-prompt">
        <div className="locate-prompt__wrapper">
          <p>No localization data available</p>
        </div>
      </div>
    )
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="locate-prompt">
        <div className="locate-prompt__wrapper">
          <div className="locate-prompt__title">
            <h1>VERIFY YOUR LOCATION</h1>
          </div>

          <div className="locate-prompt__content">
            <div className="locate-prompt__column">
              <div className="locate-prompt__step-number">1</div>
              <p className="locate-prompt__instruction">
                From a mobile device or computer, visit
              </p>
              <p className="locate-prompt__url">
                {currentState.authorization_url}
              </p>
              <img
                src={currentState.qr_code_url}
                alt="QR Code for location verification"
                className="locate-prompt__qr-code"
              />
            </div>

            <div className="locate-prompt__column">
              <div className="locate-prompt__step-number">2</div>
              <p className="locate-prompt__instruction">Enter code:</p>
              <p className="locate-prompt__code">{currentState.code}</p>
              <p className="locate-prompt__info">
                Once your location is verified, this screen will automatically
                update within 30 seconds
              </p>
              <div className="locate-prompt__new-code">
                <span className="locate-prompt__new-code-text">
                  Code not working?
                </span>
                <FocusableButtonTS
                  className="locate-prompt__new-code-button"
                  focusKey={GET_NEW_CODE_BUTTON_FOCUS_KEY}
                  onEnterPress={handleGetNewCode}
                  stealFocus
                >
                  Get New Code
                </FocusableButtonTS>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default LocatePrompt
